import type { calculateMatches } from "@kalkulacka-one/app";
import { AnswersStoreContext, useCalculator } from "@kalkulacka-one/app/client";
import { saveSessionDataWithBeacon } from "@kalkulacka-one/next/api";

import { useContext, useEffect, useRef } from "react";

import { reportError } from "@/lib/monitoring";

export type UseAutoSaveOptions = {
  matches?: ReturnType<typeof calculateMatches>;
  enabled?: boolean;
};

export function useAutoSave({ matches, enabled = true }: UseAutoSaveOptions = {}) {
  const store = useContext(AnswersStoreContext);
  const calculator = useCalculator();
  const savingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const save = async () => {
      if (savingRef.current) return;
      savingRef.current = true;

      try {
        const answers = store?.getState().answers;

        if (!answers || answers.length === 0) {
          return;
        }

        if (matches) {
          const hasValidMatches = matches.some((match) => match.match !== undefined);
          if (!hasValidMatches) {
            return;
          }
        }

        await saveSessionDataWithBeacon(calculator.id, answers, matches, calculator.version);
      } catch (error) {
        reportError(error);
      } finally {
        savingRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") save();
    };

    const handlePageHide = () => save();

    document.addEventListener("visibilitychange", handleVisibilityChange, { passive: true });
    window.addEventListener("pagehide", handlePageHide, { passive: true });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [enabled, matches, calculator.id, calculator.version, store]);
}
